/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
/// <reference types="vite/client" />

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { services } from "@/assets/data/servicesDetails";

const ServicesInquiryForm = () => {
  
  const { t, language } = useLanguage();
  const isRTL = language === "ar";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    service: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    const serviceForSlack = services.find(s => s.title === formData.service) 
    ? t(formData.service) 
    : formData.service === "multiple" 
      ? t("multiple_services") 
      : formData.service === "not-sure" 
        ? t("not_sure") 
        : formData.service; 

    let success = false;

    try {
      const response = await fetch("/api/service-inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          service: serviceForSlack,
        }),
      });

      success = response.ok;
    } catch (error) {
      console.error("Error submitting services inquiry:", error);
    }

    if (success) {
      setSubmitStatus("success");
      setFormData({ name: "", email: "", company: "", service: "", message: "" });
    } else {
      setSubmitStatus("error");
    }
    setIsSubmitting(false);
  };

  return (
    <section
      id="contact-us"
      className={`py-16 bg-gray-50 dark:bg-nexus-dark ${isRTL ? "rtl text-right" : "ltr text-left"}`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="container">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl font-bold text-nexus-gray dark:text-nexus-light mb-4">
            {t("services_contact_title")}
          </h2>
          <p className="text-gray-600 dark:text-nexus-light">{t("services_contact_description")}</p>
        </div>

        <div className="max-w-xl mx-auto bg-white p-8 rounded-lg shadow-sm border border-gray-200">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className={`block text-sm font-medium text-nexus-gray mb-1 ${isRTL ? "text-right" : ""}`}>
                  {t("services_contact_form.full_name_label")}
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  /*here*/
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                  placeholder={t("services_contact_form.full_name_placeholder")}
                />
              </div>
              <div>
                <label htmlFor="email" className={`block text-sm font-medium text-nexus-gray mb-1 ${isRTL ? "text-right" : ""}`}>
                  {t("services_contact_form.email_label")}
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  /*here*/
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="company" className={`block text-sm font-medium text-nexus-gray mb-1 ${isRTL ? "text-right" : ""}`}>
                {t("services_contact_form.company_label")}
              </label>
              <input
                type="text"
                id="company"
                value={formData.company}
                onChange={handleChange}
                /*here*/
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                placeholder={t("services_contact_form.company_placeholder")}
              />
            </div>

            <div>
              <label htmlFor="service" className={`block text-sm font-medium text-nexus-gray mb-1 ${isRTL ? "text-right" : ""}`}>
                {t("services_contact_form.services_label")}
              </label>
              <select
                id="service"
                value={formData.service}
                onChange={handleChange}
                required
                /*here*/
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
              
              >
                <option value="">{t("services_contact_form.services_placeholder")}</option>
                {services.map((service, index) => (
                  <option key={index} value={service.title}>
                    {t(service.title)}
                  </option>
                ))}
                <option value="multiple">{t("multiple_services")}</option>
                <option value="not-sure">{t("not_sure")}</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className={`block text-sm font-medium text-nexus-gray mb-1 ${isRTL ? "text-right" : ""}`}>
                {t("services_contact_form.message_label")}
              </label>
              <textarea
                id="message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                /*here*/
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                placeholder={t("services_contact_form.message_placeholder")}
              ></textarea>
            </div>

            <Button type="submit" disabled={isSubmitting} className="w-full bg-nexus-brand hover:bg-nexus-brand/90 dark:text-nexus-light">
              {isSubmitting ? t("services_contact_form.loading") : t("services_contact_form.submit_button")}
            </Button>
          </form>

          {submitStatus === "success" && (
            <p className="mt-4 text-green-600">{t("form_submit_success")}</p>
          )}
          {submitStatus === "error" && (
            <p className="mt-4 text-red-600">{t("form_submit_error")}</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServicesInquiryForm;
